/**
 * Utils - project file and scene resolution utilities.
 * Locates project files, loads settings, and resolves startup/default scene paths.
 */

import path from 'path';

import {
  ASSETS_FOLDER,
  BUILT_PROJECT_FOLDER,
  DEFAULT_SCENE_KEY,
  DEFAULT_SCENE_NAME,
  DEFAULT_SERVER_SCENE_KEY,
  ENGINE_VERSION_KEY,
  GAME_PROJECT_FILE_EXT,
  PROJECT_PATH_PREFIX,
  SCENE_NAME_EXT
} from '../const.js';
import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { AssetPath } from '../storage/storageProvider.js';

import type { FileItem, IStorageProvider } from '../storage/storageProvider.js';

const getSceneFiles = async (storageProvider: IStorageProvider, forEditor: boolean): Promise<FileItem[]> => {
  const root = PROJECT_PATH_PREFIX;
  const result = await storageProvider.listFiles(AssetPath.fromString(root), true);
  const sceneFiles: FileItem[] = result.files.filter(file => {
    if (forEditor && file.path.includes(BUILT_PROJECT_FOLDER)) {
      return false; // Skip files in the built project folder for editor
    }
    return file.path.endsWith(SCENE_NAME_EXT);
  });

  return sceneFiles;
};

export const findProjectFileForGame = async (storageProvider: IStorageProvider): Promise<string | null> => {
  const root = PROJECT_PATH_PREFIX;
  let result;
  try {
    // in non-editor environment, we need to include hidden files since they're in .dist
    result = await storageProvider.listFiles(AssetPath.fromString(root), true /*recursive*/, true /*includeHiddenFiles*/);
  } catch (error) {
    console.error('Failed to list files in project root:', root, error);
    return null;
  }

  // Find any .genesys-project file
  const projectFile = result.files.find(file =>
    file.name.endsWith(GAME_PROJECT_FILE_EXT)
  );
  return projectFile ? projectFile.path : null;
};

/**
 * Loads project settings from a `.genesys-project` file.
 *
 * @param storageProvider - The storage provider to use for file operations
 * @param projectFilePath - Path to the `.genesys-project` file
 * @returns A tuple of the resolved project file path and its parsed JSON content
 *
 * @remarks
 * The settings object is typed as `Record<string, unknown>` because the project file
 * is untyped JSON; callers are responsible for narrowing individual values they read.
 */
export const loadProjectSettings = async (storageProvider: IStorageProvider, projectFilePath: string): Promise<[string | null, Record<string, unknown>]> => {
  const projectFile = projectFilePath;
  if (!projectFile) {
    console.log('loadProjectSettings is not provided a project file');
    return [null, {}];
  }

  let projectSettings: Record<string, unknown> = {};
  if (projectFile) {
    console.log('loadProjectSettings found projectFile:', projectFile);

    try {
      // Load the project file as JSON
      const data = await storageProvider.downloadFileAsJson(AssetPath.fromString(projectFile));
      projectSettings = data as Record<string, unknown>;
    } catch (error) {
      console.warn('Failed to load project file:', projectFile, error);
    }
  }

  return [projectFile, projectSettings];
};

export async function getProjectEngineVersion(storageProvider: IStorageProvider, projectFilePath: string): Promise<string | null> {
  const [_, projectSettings] = await loadProjectSettings(storageProvider, projectFilePath);
  return (projectSettings[ENGINE_VERSION_KEY] as string) ?? null;
}
interface GetStartupSceneOptions {
  forEditor: boolean;
  projectFilePath: string;
}

/**
 * Resolves a scene path from parsed project settings for a given config key.
 *
 * @param projectSettings - Parsed project settings (untyped JSON object from storage)
 * @param configKey - The project settings key to look up
 * @param projectDir - The directory containing the project file, for resolving relative paths
 * @returns The resolved scene path string, or null if the key is absent
 */
const resolveScenePathFromSettings = (
  projectSettings: Record<string, unknown>,
  configKey: string,
  projectDir: string
): string | null => {
  if (!(configKey in projectSettings)) {
    return null;
  }
  // projectSettings is parsed from an untyped JSON file; all scene path values in the
  // project format are strings, so this cast is safe.
  const scenePath = AssetPath.withForwardSlashes(projectSettings[configKey] as string);
  const normalizedProjectDir = AssetPath.withForwardSlashes(projectDir);
  // Use @project/-prefixed paths as-is; the storage provider resolves them.
  if (scenePath.startsWith('@project/')) {
    return scenePath;
  }
  // Otherwise join with the project directory to make the path absolute.
  if (scenePath.startsWith(`${normalizedProjectDir}/`) || scenePath === normalizedProjectDir) {
    return scenePath;
  }
  return AssetPath.join(normalizedProjectDir, scenePath);
};

/**
 * Gets the default scene path from project settings (defaultScene).
 * This is used when reloading after disconnecting from a multiplayer server.
 *
 * @param storageProvider - The storage provider to use for file operations
 * @param projectFilePath - Path to the .genesys-project file
 * @returns The default scene path, or null if not found
 */
export const getDefaultScene = async (storageProvider: IStorageProvider, projectFilePath: string): Promise<string | null> => {
  try {
    const [projectFile, projectSettings] = await loadProjectSettings(storageProvider, projectFilePath);
    if (!projectFile || !projectSettings) {
      return null;
    }

    const projectDir = AssetPath.withForwardSlashes(path.dirname(projectFile));
    const defaultScenePath = resolveScenePathFromSettings(projectSettings, DEFAULT_SCENE_KEY, projectDir);

    if (defaultScenePath) {
      if (await storageProvider.exists(AssetPath.fromString(defaultScenePath))) {
        console.log(`getDefaultScene found default scene from project file (${DEFAULT_SCENE_KEY}):`, defaultScenePath);
        return defaultScenePath;
      }
      console.warn(`[getDefaultScene] Configured default scene path does not exist: ${defaultScenePath}`);
    }
  } catch (error) {
    console.warn('Failed to get default scene from project settings:', error);
  }

  return null;
};

export const getStartupScene = async (storageProvider: IStorageProvider, options: GetStartupSceneOptions): Promise<string | null> => {
  try {
    const forEditor = options.forEditor;

    // Step 1: Look for .genesys-project file in the project root
    const [projectFile, projectSettings] = await loadProjectSettings(storageProvider, options.projectFilePath);
    if (projectFile) {
      console.log('getStartupScene found project file:', projectFile, 'with settings:', projectSettings);

      // Determine which config key to use:
      // - Dedicated server: defaultServerScene (falls back to defaultScene if not present)
      // - Editor and standalone client: defaultScene
      let configKey: string;
      if (NetRuntime.isDedicatedServer()) {
        configKey = DEFAULT_SERVER_SCENE_KEY;
        if (!(projectSettings && typeof projectSettings === 'object' && configKey in projectSettings)) {
          configKey = DEFAULT_SCENE_KEY;
        }
      } else {
        configKey = DEFAULT_SCENE_KEY;
      }

      const projectDir = AssetPath.withForwardSlashes(path.dirname(projectFile));
      const startupScenePath = resolveScenePathFromSettings(projectSettings, configKey, projectDir);

      if (startupScenePath && await storageProvider.exists(AssetPath.fromString(startupScenePath))) {
        console.log(`getStartupScene found startup scene from project file (${configKey}):`, startupScenePath);
        return startupScenePath;
      }
    } else {
      console.log('getStartupScene found no project file');
    }

    // Step 2: Fallback to default scene file
    const root = PROJECT_PATH_PREFIX;
    const defaultScenePath = path.join(root, ASSETS_FOLDER, DEFAULT_SCENE_NAME);
    console.log('getStartupScene checking default scene path:', defaultScenePath);
    if (await storageProvider.exists(AssetPath.fromString(defaultScenePath))) {
      return defaultScenePath;
    }

    const legacyDefaultScenePath = path.join(root, 'default.scene.json');
    console.log('getStartupScene checking legacy default scene path:', legacyDefaultScenePath);
    if (await storageProvider.exists(AssetPath.fromString(legacyDefaultScenePath))) {
      return legacyDefaultScenePath;
    }
    console.log(`getStartupScene default scene not found: ${defaultScenePath} or ${legacyDefaultScenePath}. opening the latest scene file`);

    // Step 3: Fallback to the latest scene file
    const sceneFiles = await getSceneFiles(storageProvider, forEditor);
    if (sceneFiles.length === 0) {
      return null; // No scene files found
    }

    // Sort by modification time to get the latest one
    // Note: FileItem has modifiedTime property
    const sortedSceneFiles = sceneFiles.sort((a, b) => {
      const aTime = a.modifiedTime ? new Date(a.modifiedTime).getTime() : 0;
      const bTime = b.modifiedTime ? new Date(b.modifiedTime).getTime() : 0;
      return bTime - aTime; // Sort in descending order (latest first)
    });

    return sortedSceneFiles[0].path;

  } catch (error) {
    console.error('Failed to determine startup scene:', error);
    return null;
  }
};
